declare type Locale = string;

/**
 * ⚠️ **이 객체에 시크릿을 넣지 마라.** (#262)
 *
 * 이 모듈은 15개 이상의 `'use client'` 컴포넌트가 `ROUTES` 등을 쓰려고 import
 * 한다. 그래서 여기 `NEXT_PUBLIC_*` 환경변수를 넣으면 Next 가 빌드 시점에
 * 문자열로 인라인해 **객체 전체가 공개 클라이언트 번들로 나간다.** 실제로 그렇게
 * 토스 라이브 위젯 시크릿 키가 유출됐다.
 *
 * 서버에서만 쓸 값은 `#/lib/toss-secrets` 처럼 `import 'server-only'` 를 붙인
 * 모듈에 둔다. (접두사 없는 변수는 클라이언트에서 그냥 빈 값이 되므로, 여기
 * 남아 있는 GOOGLE_CLIENT_SECRET 등은 번들로 나가지는 않는다.)
 */
export const ENV = {
  NEXT_PUBLIC_API_URL:
    process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000',
  NEXTAUTH_URL: process.env.NEXTAUTH_URL || 'http://localhost:3000',
  NEXT_PUBLIC_HOMEPAGE_URL:
    process.env.NEXT_PUBLIC_HOMEPAGE_URL || 'http://localhost:3000',
  GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID || '',
  GOOGLE_CLIENT_SECRET: process.env.GOOGLE_CLIENT_SECRET || '',
  KAKAO_CLIENT_ID: process.env.KAKAO_CLIENT_ID || '',
  KAKAO_CLIENT_SECRET: process.env.KAKAO_CLIENT_SECRET || '',
  NEXT_PUBLIC_KAKAO_API_KEY: process.env.NEXT_PUBLIC_KAKAO_API_KEY || '',
  NAVER_CLIENT_ID: process.env.NAVER_CLIENT_ID || '',
  NAVER_CLIENT_SECRET: process.env.NAVER_CLIENT_SECRET || '',

  NEXTAUTH_SECRET: process.env.NEXTAUTH_SECRET || '',
  GMAIL_EMAIL_ADDRESS: process.env.GMAIL_EMAIL_ADDRESS || '',
  GMAIL_APP_PASSWORD: process.env.GMAIL_APP_PASSWORD || '',
  SENDER_EMAIL_ADDRESS: process.env.SENDER_EMAIL_ADDRESS || '',
  GOOGLE_APPLICATION_CREDENTIALS:
    process.env.GOOGLE_APPLICATION_CREDENTIALS || 'key.json',
  GOOGLE_BAREUN_APPLICATION_CREDENTIALS:
    process.env.GOOGLE_BAREUN_APPLICATION_CREDENTIALS || 'bareun_key.json',
  GCS_PUBLIC_BUCKET_URL: `https://storage.googleapis.com/${process.env.GCS_PUBLIC_STORAGE || ''}`,
  GCS_PUBLIC_STORAGE: process.env.GCS_PUBLIC_STORAGE || '',
  GCS_PRIVATE_STORAGE: process.env.GCS_PRIVATE_STORAGE || '',
  GCS_VERISON_BUCKET: process.env.GCS_VERISON_BUCKET || 'bareun-release',
  IS_DEVELOPMENT:
    process.env.NODE_ENV === 'development' && process.env.ENV_NAME === 'dev',
  NEXT_PUBLIC_WEBHOOK_GOOGLE_CHAT_ERROR:
    process.env.NEXT_PUBLIC_WEBHOOK_GOOGLE_CHAT_ERROR || '',
  // 형태소 분석·교정 서버 주소.
  //
  // NEXT_PUBLIC_ 을 먼저 본다 — analyze/tokenize 는 actions/server/fetch.ts 가
  // **브라우저에서** 실행하므로(파일 상단 'use server' 가 주석 처리돼 있다) 접두사가
  // 없는 환경변수는 클라이언트 번들에 주입되지 않아 값이 통째로 무시되고 기본값으로
  // 떨어진다. 다른 서버(로컬·카나리)를 붙여 시험할 때 이걸 모르면 계속 운영 서버로
  // 나간다. 서버 전용 경로(lib/grpc-service.ts)는 둘 중 아무거나 읽어도 된다.
  //
  // 스킴을 붙여도 된다: 'http://127.0.0.1:5656' 처럼 주면 TLS 없는 로컬 서버로 가고,
  // 'canary.bareun.ai' 처럼 스킴이 없으면 https 로 붙는다(getBareunBaseUrl 참고).
  GRPC_BAREUN_ADDRESS:
    process.env.NEXT_PUBLIC_GRPC_BAREUN_ADDRESS ||
    process.env.GRPC_BAREUN_ADDRESS ||
    'api.bareun.ai',
  GRPC_BAREUN_REG: process.env.GRPC_BAREUN_REG || 'api.bareun.ai',
  BAREUN_API_KEY:
    process.env.BAREUN_API_KEY || 'koba-T4EA55Q-T7XU36Q-VKG5AEI-577PJ5Q',

  // ── 토스페이먼츠 공개 클라이언트 키 ──
  //
  // 하드코딩 폴백은 두지 않는다. 종전에는 test 키가 박혀 있어서, 빌드 때
  // 환경변수가 빠지면 **라이브인 척 조용히 test 키로 돌아갔다**. #262 키 교체
  // 사고 때 이 구조가 원인 파악을 늦췄다. 값이 없으면 SDK 초기화가 실패하고
  // 콘솔에 에러가 남으므로 "환경변수 누락"과 "키 무효"를 구분할 수 있다.
  // (server 시크릿 쪽도 같은 정책 — lib/toss-secrets.ts 참고)
  //
  // ⚠️ NEXT_PUBLIC_* 는 `next build` 시점에 번들로 인라인된다. 런타임
  //    환경변수로 못 바꾸므로 값 변경 시 반드시 재빌드해야 한다.
  //    출처: web/client/.env.staging (Dockerfile.prob 이 구워 넣음)

  // 주문서형·결제창형(위젯) 클라이언트 키 — 계정 공통
  NEXT_PUBLIC_TOSS_CLIENT_KEY: process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY || '',

  // API 개별 연동 — 정기결제 상점(bill_bareupusj). 빌링키 등록에 쓰인다.
  NEXT_PUBLIC_TOSS_MID: process.env.NEXT_PUBLIC_TOSS_MID || '',
  NEXT_PUBLIC_TOSS_API_CLIENT_KEY:
    process.env.NEXT_PUBLIC_TOSS_API_CLIENT_KEY || '',
  NEXT_PUBLIC_TOSS_API_VERSION:
    process.env.NEXT_PUBLIC_TOSS_API_VERSION || '2024-06-01'
};
// 5757 5658
export const ROUTES = {
  ROOT: '/',
  REGISTER: '/register',
  HOME: '/home',
  PROFILE: '/profile',
  LOGIN: '/login',
  FORGOT_PASSWORD: '/forgot-password',
  LOGOUT: '/logout',
  ADD: 'add',
  STAT: '/stat',
  SOCIAL_ACCOUNT: '/social-account',
  BILLING_CARD: '/billing-card',
  ORDER_LIST: '/order-list',
  API_KEY: '/api-key',
  APPS: '/apps',
  DEVICES: '/devices',
  EMAIL_VALIDATED: '/email-validated',
  UNREGISTER: '/unregister',
  PASSWORD_CHANGE: '/password-change',
  DOWNNLOAD: '/download',
  EXTENSION: '/extension',
  HANCOM_ADDONS: '/hancom-addons', // 한컴 애드온즈 기능 설명 (#309)
  HANCOM_ADDONS_VERSIONS: '/hancom-addons/versions',
  HANCOM_ADDONS_DOWNLOAD: '/hancom-addons/download',
  HANCOM_ADDONS_PANE: '/hancom-addons/pane', // 한글 작업창 (#301)
  HANCOM_ADDONS_START: '/hancom-addons/start', // 작업창의 로그인·가입 진입점(가입 경로 표시, #301)
  HANCOM_ADDONS_LOGIN: '/hancom-addons/login', // 작업창 전용 로그인(사이트 메뉴 없음, #301)
  HANCOM_ADDONS_REGISTER: '/hancom-addons/register', // 작업창 전용 회원 가입(사이트 메뉴 없음, #301)
  DOWNNLOAD_VERSION: '/download/versions',
  UPDATE_LOGS: '/update-logs',
  TRY: '/try',
  ANALYZE: '/analyze',
  CUSTOM_DICT: '/custom-dict',
  TOKENIZE: '/tokenize',
  DICT_SEARCH: '/dict-search',
  CORRECT: '/correct',
  SPELL_CHECK: '/spell-check',
  DOCS: '/docs',
  FAQ: '/faq',
  QA: '/qa',
  QA_SPELLING_100: '/qa/collections/spelling-100',
  PRIVACY: '/privacy',
  SERVICE: '/service',
  REFUND_POLICY: '/refund-policy',
  PRODUCT: '/product',
  PAYMENT: '/product/payment',
  SUBSCRIPTION: '/subscription',
  REFUND: '/refund',
  MY_REPORTS: '/my-reports',
  NOTIFICATIONS: '/notifications', // 알림 설정 (#264 웹 푸시)
  APIUSING: '/api-using',
  ADD_BILLING_KEY: '/api/toss/billing/success',
  ADD_BILLING_KEY_FAIL: '/api/toss/billing/fail',
  PAYMENT_SUCCESS: '/api/toss/payment/success',
  PAYMENT_FAIL: '/api/toss/payment/fail',
  PRODUCT_CHECKOUT: '/product/checkout',
  ADMIN: '/admin',
  DASHBOARD: '/dashboard',
  USER_STATISTICS: '/user-statistics',
  USER_API: '/user-api',
  USERS_MANAGE: '/users',
  SUBSCRIPTIONS_MANAGE: '/subscriptions',
  PAYMENTS_MANAGE: '/payments',
  FAQ_MANAGE: '/faq-manage',
  NOTICES_MANAGE: '/notices',
  BANNER_MESSAGES_MANAGE: '/banner-messages',
  AUDIT_LOGS: '/audit-logs',
  ERROR_LOGS: '/error-logs',
  SPELLER_REPORTS: '/speller-reports',
  SITE_REVIEWS: '/site-reviews', // 맞춤법 검사기 별점·한 줄 평 관리 (#291)
  RESEARCH_APPLICATIONS: '/research-applications', // 연구목적 사용신청 심사 (#294)
  SEO_HEALTH: '/seo-health', // 검색 유입 회귀 점검 (#287)
  PUSH_MANAGE: '/push', // 관리자 푸시 알림 관리 (#264)
  API_USAGE_ANALYSIS: '/api-usage-analysis',
  SUPPORT: '/helpdesk',
  CORRECTFILE: '/correct-file'
};

export const EMAIL = {
  FROM: 'noreply@bareun.ai',
  INVITE_SUBJECT: '바른 이메일 인증 안내',
  INVITE_TITLE: '바른 이메일 인증 안내.',
  INVITE_BODY:
    '본 메일은 바른 참여를 위한 인증 메일입니다. 이 인증을 수락하시려면 아래의 버튼을 클릭하여 계정 등록 절차를 진행하세요.',
  INVITE_BUTTON: '인증 페이지로 이동',
  SUPPORT_QUESTION_SUBJECT: '바른 문의하기 답변입니다.',
  FORGOT_PASSWORD_SUBJECT: '바른 비밀번호 초기화',
  FORGOT_PASSWORD_TITLE: '바른 비밀번호 초기화 메일입니다.',
  FORGOT_PASSWORD_BODY:
    '비밀번호를 초기화 하시려면 아래의 버튼을 클릭하여 비밀번호 초기화 절차를 진행하세요.',
  FORGOT_PASSWORD_BUTTON: '비밀번호 초기화 페이지로 이동'
};

// Our basic SEO data
export type SiteMeta = {
  URL: string; // 예: 'https://bareun.ai'
  siteName: string; // 예: '바른AI'
  title: string; // 예: '바른AI'
  description: string; // 예: '바른AI 한국어 교정·분석 플랫폼'
  backgroundColor: string; // '#F5E1E6' 등
  // 구조화 데이터(Organization.logo)용 래스터 로고. 구글은 구조화 데이터 이미지로
  // SVG 를 쓰지 않으므로 webp 로 둔다.
  logoImage: string;
  og: {
    locale: string; // 'ko-KR'
    type?: 'website' | 'article';
    ogImage: string; // 절대/상대 경로 모두 가능 (실제 파일이어야 인덱싱된다)
    width: number;
    height: number;
  };
  googleSiteVerification?: string;
  naverSiteVerification?: string;
  bingSiteVerification?: string;
};

export const meta: SiteMeta = {
  URL: process.env.NEXT_PUBLIC_SITE_URL ?? 'https://bareun.ai',
  siteName: '바른AI — 한국어 맞춤법 검사기',
  title: '바른AI — 한국어 맞춤법 검사기 · 띄어쓰기 교정 · 형태소 분석기',
  description:
    '바른AI는 한국어 맞춤법·띄어쓰기·문법을 AI로 실시간 교정하고, 형태소 분석·분절·사용자 사전을 지원하는 무료 한국어 맞춤법 검사기입니다.',
  backgroundColor: '#104573',
  logoImage: '/static/images/bareun-logo.webp',
  og: {
    locale: 'ko-KR',
    type: 'website',
    // 정적 PNG 파일로 둔다. 확장자 없는 동적 라우트(/opengraph-image)는 구글
    // 이미지 인덱스에 잘 잡히지 않아, AI 개요 카드 썸네일이 페이지 안의 엉뚱한
    // 아이콘(카카오 로고)으로 대체되는 일이 있었다.
    ogImage: '/static/images/og-default.png',
    width: 1200,
    height: 630
  },
  googleSiteVerification: 'kVo-pc5aOv11zyY543EtJmRCP-zLTBc8foCYCgX5kW4',
  // 네이버 서치어드바이저(searchadvisor.naver.com) > 사이트 등록 > HTML 태그 방식
  // 소유확인 코드 발급 후 content 값을 여기에 채운다 (#203)
  // 파일 방식(public/naverc4658151fd85124bfb246cfc65b5c6d1.html)으로 확인하면 불필요
  naverSiteVerification: undefined,
  // 빙 웹마스터 도구(bing.com/webmasters) HTML Meta 태그 방식 — msvalidate.01 (#261)
  // 파일 방식(public/BingSiteAuth.xml)과 동일한 토큰
  bingSiteVerification: '1C1EA390D15818E7CD779E342A32B311'
};

export const COOKIE_KEY = {
  REGISTER: 'bareun-register'
};

export const TEXTS = {
  UNKOWN: '알 수 없음',
  NO_VALUE: '없음'
};

export const GENDER = ['남성', '여성', '미지정'];
export const PROVIDER_ID = 'cmbugw3qv00020z89oyjuwcjg';

export const spellerCount = 10000;
export const ANON_DAILY_CALL_LIMIT = 10; // 비로그인 일일 최대 API 호출 횟수
/**
 * 비로그인 최대 텍스트 길이 (자).
 *
 * 1,000자에서 2,000자로 올린다. `/correct` 의 제목과 설명이 "자기소개서·보고서·
 * 논문도 잘라 넣지 않고 통째로 검사"라고 약속하는데, 자소서는 보통 1,500자라
 * 붙여넣는 순간 막혔다. 검색해서 들어온 사람이 첫 동작에서 튕기는 셈이다.
 *
 * 같은 검색어를 놓고 붙는 인크루트 맞춤법 검사기(부산대 PnuNlp 엔진)는 글자 수
 * 제한이 없고 제목도 "자소서 통째로 오타·띄어쓰기 교정"이다.
 *
 * 비용은 `ANON_DAILY_CALL_LIMIT`(IP 당 하루 10회)가 막는다. 한 IP 가 하루에
 * 쓸 수 있는 최대치가 1만 자에서 2만 자로 늘어난다.
 */
export const ANON_MAX_TEXT_LENGTH = 2000;

/**
 * 비로그인 글자 수 제한(레거시 이름). 클라이언트 UI 가 이 이름을 쓰고 서버 액션은
 * ANON_MAX_TEXT_LENGTH 를 쓰는데, 두 값이 따로 적혀 있으면 UI 는 통과시키고 서버가
 * 거절하는 불일치가 생긴다. 별칭으로 묶어 드리프트를 원천 차단한다.
 */
export const spellerLength = ANON_MAX_TEXT_LENGTH;

/**
 * 비로그인 제한 안내 문구.
 *
 * 제약 고지("~하셔야합니다")보다 로그인 혜택을 앞세운다 — 검사기 유입 대비
 * 로그인 전환이 3.6% 에 머물러 있었다(GA 2026-07-15~08-11: 맞춤법 검사기 활성
 * 1.1만 → 로그인 396). 로그인 사용자는 글자 수가 실제로 무제한이므로
 * (text-area.tsx 의 limit=null) 이 혜택만 이야기하고, 구현되지 않은 기능
 * (교정 기록 보관 등)은 약속하지 않는다.
 */
export const ANON_TEXT_LIMIT_MESSAGE = `로그인하면 글자 수 제한 없이 검사할 수 있어요. 비로그인은 ${ANON_MAX_TEXT_LENGTH.toLocaleString()}자까지 가능합니다.`;

/**
 * 비로그인 사용량 확인이 실패했을 때의 안내 — 원인별로 문구를 나눈다. (#271)
 *
 * 같은 자리에서 터지던 오류(`Cannot read properties of undefined (reading 'allowed')`)의
 * 원인이 하나가 아니었다. 사용자가 할 수 있는 조치가 원인마다 다르므로 문구도 나눈다:
 *
 *  1. 배포 스큐 — 액션 ID 가 사라짐(서버 로그 `Failed to find Server Action`).
 *     조치는 새로고침이고, 그건 코드가 대신 한다 → RESTORED/RELOADING 문구.
 *  2. 서버 액션 내부 실패 — DB 경합·장애. 사용자가 할 일은 잠시 후 재시도 → UNAVAILABLE.
 *  3. 응답을 받지 못함 — 오프라인·네트워크 단절 → OFFLINE. (탭 이탈은 안내하지 않는다)
 */
export const ANON_USAGE_CHECK_FAILED_MESSAGE =
  '사용량 확인에 실패했습니다. 페이지를 새로고침한 뒤 다시 시도해 주세요.';

export const ANON_USAGE_UNAVAILABLE_MESSAGE =
  '일시적인 오류로 무료 사용량을 확인할 수 없습니다. 잠시 후 다시 시도해 주세요.';

export const NETWORK_OFFLINE_MESSAGE =
  '네트워크 연결이 끊겼습니다. 연결을 확인한 뒤 다시 시도해 주세요.';

export const STALE_DEPLOYMENT_RELOADING_MESSAGE =
  '새 버전이 배포되어 페이지를 새로 불러옵니다. 입력한 글은 그대로 유지됩니다.';

export const STALE_DEPLOYMENT_RESTORED_MESSAGE =
  '새 버전으로 페이지를 새로 불러왔습니다. 입력한 글을 되돌려 놓았어요 — 다시 검사해 주세요.';

/**
 * 공식 크롬 확장 "바른 맞춤법 검사기" 웹스토어 주소.
 *
 * 유입 위치별 기여도를 나누어 보려고 utm_medium 을 호출부에서 붙인다.
 * 확장 ID 가 여러 곳에 흩어지면 나중에 갱신할 때 하나를 빠뜨리므로 여기서만 정의한다.
 */
export const CHROME_EXTENSION_ID = 'neippdhejajpddhbnkgfhfmhilcbkjlm';
export const CHROME_EXTENSION_URL = `https://chromewebstore.google.com/detail/${CHROME_EXTENSION_ID}`;

/**
 * Chrome 웹스토어에 표시되는 확장 평점.
 *
 * /extension 페이지가 이 값을 화면에 그대로 보여 주고, 같은 값을
 * SoftwareApplication.aggregateRating 으로 마크업한다. 구글 리뷰 스니펫 요건이
 * "마크업한 평점이 그 페이지에서 사용자에게 보일 것"이라 둘은 반드시 같아야
 * 한다. 웹스토어 값이 바뀌면 여기만 고치면 화면과 마크업이 함께 따라간다.
 *
 * ⚠️ /correct 로 옮겨 붙이지 말 것. 그 페이지의 주 항목은 웹 맞춤법 검사기이고
 * 이 평점은 크롬 확장 것이라 항목이 어긋난다(page.tsx 주석에 전례가 있다).
 *
 * ⚠️ 알고 감수한 위험. 구글 리뷰 스니펫 정책에 "다른 웹사이트의 리뷰나 평점을
 * 집계하지 말라"(Don't aggregate reviews or ratings from other websites)가 있고,
 * 웹스토어 평점을 우리 페이지 aggregateRating 으로 내는 건 문구 그대로는 여기에
 * 걸린다. 우리 제품의 평점이고 웹스토어가 구글 자신의 플랫폼이라 해석 여지가
 * 있어, 경고나 수동 조치가 뜨는지 지켜보기로 정했다(2026-09-03). Search Console
 * 리뷰 스니펫 리포트에 문제가 잡히면 aggregateRating 만 떼고 화면 표시는 남긴다.
 */
export const CHROME_EXTENSION_RATING = {
  value: 4.2,
  count: 5,
  /** 웹스토어에서 마지막으로 값을 확인한 날 */
  checkedAt: '2026-09-03'
} as const;

/**
 * /correct 에서 직접 받는 사용자 별점 (#291).
 *
 * 전체 복사 직후에 별 5개로 묻고(`SiteReview`), 모인 값을 /correct 화면과
 * SoftwareApplication.aggregateRating 에 같은 값으로 싣는다. 우리 사이트에서
 * 우리 서비스에 대해 받은 평점이라, 위 크롬 확장 평점과 달리 "다른 사이트의 평점을
 * 집계하지 말라" 조항에 걸리지 않는다.
 */
export const SITE_REVIEW = {
  /**
   * 화면 표시와 aggregateRating 을 내기 시작하는 최소 평점 수.
   *
   * 표본이 적으면 한두 사람의 점수가 평균을 크게 흔들고, 몇 건 안 되는 평점을
   * 별점으로 내세우면 신뢰도 면에서도 정책 면에서도 불리하다. 30건이면 평균의
   * 표준오차가 0.2점 안팎으로 줄어든다(점수 표준편차 1.0 가정).
   * 값을 바꾸면 화면과 마크업이 함께 따라간다.
   */
  MIN_PUBLIC_COUNT: 30,
  /**
   * 화면에 함께 싣는 공개 한 줄 평 개수(최신순, 사용자 결정 2026-09-30).
   * 처음 몇 개만 펼쳐 보이고 나머지는 접어 둔다(`RatingSummaryBar`).
   */
  SNIPPET_LIMIT: 30,
  /** 화면에 접지 않고 바로 보이는 한 줄 평 개수. 나머지는 "더 보기" 안에 둔다. */
  SNIPPET_INLINE: 3,
  /** 1~3점 뒤 고른 문장(원문·교정문 각각)의 최대 글자 수. 비공개로만 저장한다. */
  REPORT_SENTENCE_MAX: 1000,
  /** 1~3점 뒤 사용자가 적는 설명의 최대 글자 수. 비공개로만 저장한다. */
  REPORT_NOTE_MAX: 500,
  /** 한 줄 평 최대 글자 수. 한 줄 평이지 게시판이 아니다. */
  TEXT_MAX: 100,
  /**
   * 같은 IP(해시)에서 다시 평점을 받지 않는 기간(시간).
   *
   * 영구로 막으면 회사·학교처럼 IP 하나를 여럿이 쓰는 곳에서 한 사람만 답할 수
   * 있다. 하루로 두면 같은 사람이 브라우저 기록을 지우고 거듭 누르는 것은 막고,
   * 공유 IP 의 다른 사람은 다음 날 답할 수 있다.
   */
  IP_WINDOW_HOURS: 24,
  /** 별점을 누른 뒤 한 줄 평을 덧붙일 수 있는 시간(분). 이 뒤로는 덧붙이지 못한다. */
  TEXT_ATTACH_MINUTES: 60,
  /** 화면 집계를 캐시하는 시간(초). 관리자가 공개 여부를 바꾸면 바로 무효화한다. */
  SUMMARY_REVALIDATE_SECONDS: 600
} as const;

export const chromeExtensionUrl = (medium: string) =>
  `${CHROME_EXTENSION_URL}?utm_source=bareun.ai&utm_medium=${medium}`;

/**
 * bareun 서버 baseUrl. Connect 클라이언트가 쓴다.
 *
 * ENV.GRPC_BAREUN_ADDRESS 에 스킴이 있으면 그대로 쓰고, 없으면 https 를 붙인다.
 * 로컬 서버는 TLS 가 없는 경우가 많아(예: http://127.0.0.1:5656) 스킴을 지정할
 * 길을 열어 둔다. 끝의 슬래시는 떼어 낸다 — Connect 가 경로를 붙일 때 `//` 가 되면
 * 서버가 404 를 준다.
 */
export function getBareunBaseUrl(address = ENV.GRPC_BAREUN_ADDRESS) {
  const withScheme = /^https?:\/\//i.test(address)
    ? address
    : `https://${address}`;
  return withScheme.replace(/\/+$/, '');
}
